import {
  EventDescriptionPartModel,
  IssueModel,
  AttachmentCounts,
} from 'shared/domain/issue/issueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { isIssueFieldName } from '../types/model/reportConfigGuards';
import { DocumentInDto } from 'shared/dtos/in/document';
import { WorkflowDecisionOutDto } from 'shared/domain/workflow/types';
import { isTextDescriptionPart } from 'shared/types/eventDescription';
import { HashMap } from 'shared/types/commonView';
import {
  attachmentCountsToPdfCounts,
  classifyDocumentsForPdfEstimate,
  emptyPdfDocumentCounts,
  PdfDocumentCounts,
} from './classifyDocumentsForPdfEstimate';
import { ReportIssueStatsOverrides } from './reportIssueStats';

export type ReportMessageEstimateEntry = {
  textChars: number;
  documents: PdfDocumentCounts;
};

export type ReportIssueEstimateRow = {
  processId: string;
  issue: IssueModel;
  primaryDocuments: PdfDocumentCounts;
  linkedDocuments: PdfDocumentCounts;
  events: ReportMessageEstimateEntry[];
  emails: ReportMessageEstimateEntry[];
  decisions: ReportMessageEstimateEntry[];
};

// Document arrays are optional on these models. A source may still carry the raw `documents`
// arrays, while the worker's issue reads omit them and ship the compact `attachmentCounts` of
// each message instead; `documentCountsOf` reads whichever is present. The issue's own
// attachments, and in the open issue view those of each message, come through `overrides`:
// counted from the view's documents, or by the worker for a report of many issues.
type IssueEventWithDocuments = IssueModel['events'][number] & {
  documents?: DocumentInDto[];
};
type IncomingEmailWithDocuments = IssueModel['incomingEmailMessages'][number] & {
  documents?: DocumentInDto[];
};

// Prefer the raw `documents` array when present; otherwise fall back to the compact
// `attachmentCounts` of the worker's issue reads.
function documentCountsOf(
  documents: DocumentInDto[] | undefined,
  attachmentCounts: AttachmentCounts | undefined
): PdfDocumentCounts {
  if (documents !== undefined) {
    return classifyDocumentsForPdfEstimate(documents);
  }
  return attachmentCountsToPdfCounts(attachmentCounts);
}

type IssueModelWithPayload = IssueModel & {
  primaryData: IssueModel['primaryData'] & {
    documents?: DocumentInDto[];
  };
  events: IssueEventWithDocuments[];
  incomingEmailMessages: IncomingEmailWithDocuments[];
};

function eventTextLength(event: {
  title?: string;
  description?: EventDescriptionPartModel[];
}): number {
  return (
    (event.title?.length ?? 0) +
    (event.description?.reduce(
      (sum, part) =>
        sum + (isTextDescriptionPart(part) ? part.text.length : 0),
      0
    ) ?? 0)
  );
}

function decisionTextLength(decision: WorkflowDecisionOutDto): number {
  return (
    decision.description?.reduce(
      (sum, part) =>
        sum + (isTextDescriptionPart(part) ? part.text.length : 0),
      0
    ) ?? 0
  );
}

function buildLinkedDocumentCounts(
  issue: IssueModelWithPayload
): PdfDocumentCounts {
  const linked = (issue.linkedDocumentation ?? []).filter((item) => !item.deleted);
  return {
    images: linked.length,
    nonImages: 0,
    all: linked.length,
    descriptionChars: linked.reduce(
      (sum, item) => sum + (item.description?.length ?? item.name?.length ?? 0),
      0
    ),
  };
}

function buildPrimaryDocumentCounts(
  issue: IssueModelWithPayload,
  overrides?: ReportIssueStatsOverrides
): PdfDocumentCounts {
  const counts =
    overrides?.primaryDocuments ?? classifyDocumentsForPdfEstimate(issue.primaryData.documents ?? []);

  // An issue known only by its main image still prints that image.
  if (counts.all === 0 && issue.mainImage) {
    return { ...counts, images: 1, all: 1 };
  }
  return counts;
}

type MessageWithDocuments = {
  _id: string;
  deleted?: boolean;
  documents?: DocumentInDto[];
  attachmentCounts?: AttachmentCounts;
};

/**
 * The entries of the messages of one kind, without the deleted ones. `countedById`:
 * the documents of each message counted in the open issue view, which win over
 * what the model carries; a message missing from it has none.
 */
function buildMessageEntries<TMessage extends MessageWithDocuments>(
  messages: TMessage[] | undefined,
  textLength: (message: TMessage) => number,
  countedById: HashMap<PdfDocumentCounts> | undefined
): ReportMessageEstimateEntry[] {
  return (messages ?? [])
    .filter((message) => !message.deleted)
    .map((message) => ({
      textChars: textLength(message),
      documents: countedById
        ? (countedById[message._id] ?? emptyPdfDocumentCounts())
        : documentCountsOf(message.documents, message.attachmentCounts),
    }));
}

function emailTextLength(email: IncomingEmailWithDocuments): number {
  return (email.subject?.length ?? 0) + (email.incomingEmailMessageContent?.length ?? 0);
}

function buildDecisionEntries(issue: IssueModelWithPayload): ReportMessageEstimateEntry[] {
  return (issue.workflowDecisions ?? []).map((decision) => ({
    textChars: decisionTextLength(decision),
    documents: {
      images: 0,
      nonImages: 0,
      all: 0,
      descriptionChars: 0,
    },
  }));
}

export function buildReportIssueEstimateRow(
  issue: IssueModel,
  overrides?: ReportIssueStatsOverrides
): ReportIssueEstimateRow {
  // Optional payload fields make `IssueModel` assignable here without a cast.
  const payload: IssueModelWithPayload = issue;

  return {
    processId: issue.process,
    issue,
    primaryDocuments: buildPrimaryDocumentCounts(payload, overrides),
    linkedDocuments: buildLinkedDocumentCounts(payload),
    events: buildMessageEntries(payload.events, eventTextLength, overrides?.eventDocuments),
    emails: buildMessageEntries(payload.incomingEmailMessages, emailTextLength, overrides?.emailDocuments),
    decisions: buildDecisionEntries(payload),
  };
}

export function buildReportIssueEstimateRows(
  issueModels: IssueModel[],
  overrides?: ReportIssueStatsOverrides[]
): ReportIssueEstimateRow[] {
  return issueModels.map((issue, index) =>
    buildReportIssueEstimateRow(issue, overrides?.[index])
  );
}

export function getAvailableFieldsForProcess(
  processId: string,
  visibleFieldsByProcess: Record<string, { fieldName: string }[]>,
  staticFieldNamesForProcess: IssueFieldNames[]
): Set<IssueFieldNames> {
  const dynamicFields = (visibleFieldsByProcess[processId] ?? [])
    .map((field) => field.fieldName)
    .filter(isIssueFieldName);

  return new Set<IssueFieldNames>([
    ...staticFieldNamesForProcess,
    ...dynamicFields,
  ]);
}
